/**
 * What the detail's Performance draws from Voice's figures (VOX-318): the
 * period filter, the KPI row, the funnel, how the calls ended and the runway
 * of what is left.
 *
 * Voice counts (`GET …/performance`, `services/campaignPerformanceClient.ts`);
 * this module only picks, divides where Voice didn't, and names. It never
 * estimates a figure Voice doesn't give: no source is `null`, drawn as "—".
 *
 * Every label here is an i18n KEY of the `campaigns` namespace.
 */

import type { CampaignObjectiveId, Pacing } from '../types';
import { objectiveById } from './objectives';
import { foldOutcomeLabel } from './foldLabel';
import { computeRunway, type Runway } from './runway';
import { ownQuotaPace } from './pacing';
import { formatCompact, formatCount, formatMoney, formatNumber, formatPercent } from './format';

/* ─── The period ─────────────────────────────────────────────────────────── */

/**
 * The periods of the filter. `all` first and the default: a
 * campaign is read whole and cut from there. `month` is the current month, the
 * cut reported outwards — not "the last 30 days".
 */
export const PERFORMANCE_PERIODS = ['all', '1', '7', '15', '30', '90', 'month'] as const;
export type PerformancePeriod = (typeof PERFORMANCE_PERIODS)[number];

/** Date-only arithmetic in UTC: no browser zone, no DST hour can move the day. */
const isoPlusDays = (iso: string, days: number): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * Today ("YYYY-MM-DD") in the CAMPAIGN's time zone, not the browser's: Voice
 * reads `from`/`to` and draws its calendar in that zone (`filters.timezone`),
 * so a supervisor in Madrid looking at a Mexico City campaign cuts the same
 * days the campaign lived. A zone `Intl` doesn't know falls back to UTC, as
 * Voice does for a campaign without one.
 */
export function todayIn(timeZone: string | null | undefined, now: Date = new Date()): string {
  const read = (zone: string) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  try {
    return read(timeZone || 'UTC');
  } catch {
    return read('UTC');
  }
}

/** From which date ("YYYY-MM-DD") a period counts; `null` = no cut. */
export function periodCutoff(period: PerformancePeriod, today: string): string | null {
  if (period === 'all') return null;
  if (period === 'month') return `${today.slice(0, 7)}-01`;
  return isoPlusDays(today, -(Number(period) - 1));
}

/**
 * The period as Voice takes it: both ends inclusive, in the campaign's zone.
 * `null` for the whole campaign — no `from`, no `to`.
 */
export function periodRange(
  period: PerformancePeriod,
  today: string,
): { from: string; to: string } | null {
  const from = periodCutoff(period, today);
  return from === null ? null : { from, to: today };
}

/* ─── The figures, as Voice counts them ─────────────────────────────────── */

/**
 * Calls of the slice (Voice `CallCounts`). Each CALL counts, retries included:
 * `connected` is `answered` or `hung-up`, `failed` is `failed` or `canceled`.
 */
export interface PerformanceCalls {
  total: number;
  connected: number;
  voicemail: number;
  noAnswer: number;
  busy: number;
  failed: number;
  durationSec: number;
  minutes: number;
  /** Mean of the CONNECTED calls; `null` when none connected. */
  avgDurationSec: number | null;
}

/**
 * Contacts of the slice. With a period, only the ones whose LAST call fell in
 * it — so `calls.connected` may be larger than `contacted`.
 */
export interface PerformanceContacts {
  /** Without the suppressed. */
  total: number;
  /** Their last call connected. */
  contacted: number;
  /** Finished: `done` or `invalid`. */
  closed: number;
  /** Their outcome is of the success group (the rule of `…/outcomes`). */
  conversions: number;
  /** Percentages with one decimal (26.3, not 0.263); `null` when the base is 0. */
  conversionRate: number | null;
  contactRate: number | null;
}

/** `audience`, `contacted`, `stage:<i>` (index in `results.stageLabels`) or `conversion`. */
export interface PerformanceFunnelRow {
  key: string;
  /** The stage's stored label; `null` on the fixed rows, which the front names. */
  label: string | null;
  count: number;
  /** Percentages; `null` when the base is 0 (and on the first row). */
  ofPrevious: number | null;
  ofAudience: number | null;
}

/** One numeric column of the objective (`committedAmount`, `score`…). */
export interface PerformanceObjectiveMetric {
  key: string;
  header: string;
  /** Contacts with a value. */
  count: number;
  sum: number;
  avg: number | null;
}

/**
 * One subagent's share of a day. PROPOSED to Voice, not served yet (pending
 * Voice): `calendar[].bySubagent`. `subagentId: null` gathers the calls of
 * contacts with no subagent.
 */
export interface PerformanceDaySubagent {
  subagentId: string | null;
  calls: number;
  conversions: number;
}

/** One local day with activity, in the campaign's zone. */
export interface PerformanceDay {
  day: string;
  calls: number;
  connected: number;
  durationSec: number;
  conversions: number;
  /**
   * The day split by subagent — PROPOSED, pending Voice. Absent (`undefined`)
   * while Voice doesn't send it: then the day tooltip shows no split, never an
   * estimated one.
   */
  bySubagent?: PerformanceDaySubagent[];
}

/** The same counts per subagent; `subagentId: null` gathers the contacts with none. */
export interface PerformanceSubagent {
  subagentId: string | null;
  name: string | null;
  skillName: string | null;
  audienceId: string | null;
  calls: PerformanceCalls;
  contacts: number;
  contacted: number;
  conversions: number;
  conversionRate: number | null;
}

/** `GET /voice/campaigns/{chatbotId}/{campaignId}/performance` (VOX-318). */
export interface CampaignPerformance {
  filters: {
    audienceId: string | null;
    subagentId: string | null;
    from: string | null;
    to: string | null;
    timezone: string;
  };
  calls: PerformanceCalls;
  contacts: PerformanceContacts;
  funnel: PerformanceFunnelRow[];
  objectiveMetrics: PerformanceObjectiveMetric[];
  calendar: PerformanceDay[];
  bySubagent: PerformanceSubagent[];
  callsScanned: number;
  contactsScanned: number;
  /** A read stopped at its cap (100 pages or 20 s): the figures cover what was read. */
  partial: boolean;
}

/** A Voice percentage (26.3) as the ratio the formatters take (0.263); `null` stays. */
export const fromPercent = (pct: number | null): number | null => (pct === null ? null : pct / 100);

/* ─── Formatting ─────────────────────────────────────────────────────────── */

export const fmtInt = (n: number, lang: string | undefined): string => formatCount(n, lang);
export const fmtPct = (n: number, lang: string | undefined): string => formatPercent(n, lang);
export const fmtMoney = (n: number, lang: string | undefined): string => formatMoney(n, lang);

/** The same amount, short, for the big number of a card: "$10.6M" fits, "$10,550,498.98" doesn't. */
export const fmtMoneyShort = (n: number, lang: string | undefined): string => {
  if (Math.abs(n) >= 10_000) return `$${formatCompact(n, lang)}`;
  return fmtMoney(n, lang);
};

/** Minutes as "m:ss": nobody says they talked 2.3 minutes. */
export const fmtMinutes = (minutes: number): string => {
  const total = Math.max(0, Math.round(minutes * 60));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/** Safe division for rates over possibly-zero denominators. */
export const rate = (num: number, den: number): number => (den > 0 ? num / den : 0);

/**
 * A percentage with the precision the number needs: under
 * 1% one more decimal — 7 of 2,051 is 0.34%, not "0.0%" — and a round ".0"
 * drops so the column says 100%, not "100.0%".
 */
export function pctLabel(n: number, lang: string | undefined): string {
  const v = n * 100;
  return formatPercent(n, lang, { min: 0, max: v > 0 && v < 1 ? 2 : 1 });
}

/* ─── KPIs ───────────────────────────────────────────────────────────────── */

export type KpiFormat = 'int' | 'pct' | 'money' | 'money-short' | 'minutes' | 'decimal';

export type KpiId =
  | 'contact-rate'
  | 'escalation-rate'
  | 'promised-amount'
  | 'dispute-rate'
  | 'qualification-rate'
  | 'completion-rate'
  | 'csat'
  | 'delivery-rate'
  | 'reason-identified'
  | 'uncooperative'
  | 'offer-reach'
  | 'avg-duration'
  | 'cost-per-contact';

export interface Kpi {
  id: KpiId;
  /** `performance.kpis.<id>`. */
  label: string;
  /** `null` = no source or no denominator: drawn as a dash, never as a zero. */
  value: number | null;
  format: KpiFormat;
  /** A second line, with its own format. */
  hint?: { value: number; format: KpiFormat };
  /** Its source is still loading (the outcome counts): a bone, not a dash. */
  pending?: boolean;
}

/**
 * What a few cards read outside `/performance`, both WITHOUT filters — so both
 * come only when the view is the whole campaign, and the cards that need them
 * say "—" on a slice rather than mix two populations.
 */
export interface KpiExtras {
  /**
   * `GET …/outcomes` (VOX-315): contacts per outcome key, and the ones that
   * talked (success + rejection groups) as the denominator. `null` = not read
   * (a slice, or the read failed).
   */
  outcomes: { byKey: Record<string, number>; talked: number } | null;
  /** The outcome counts are still loading. */
  outcomesPending: boolean;
  /**
   * The campaign's spend in USD (Voice `totalCostUsd`, campaign GET); `null` on
   * a slice, and `null` when it is not known — Voice never accumulates it, so a
   * 0 after calls is unknown, not free (`domain/cost.ts` → `knownCost`).
   */
  costUsd: number | null;
}

export const NO_EXTRAS: KpiExtras = { outcomes: null, outcomesPending: false, costUsd: null };

/** The outcome keys a card reads on its own (`domain/objectives.ts` dispositions). */
export const ESCALATE_OUTCOME = 'objectives.shared.disposition.escalate';
export const DISPUTE_OUTCOME = 'objectives.collections.disposition.disputesBalance';
export const UNCOOPERATIVE_OUTCOME = 'objectives.retention.disposition.uncooperative';
const QUALIFIED_STAGE = 'objectives.sales.stage.qualified';
const OFFER_STAGE = 'objectives.retention.stage.offer';
const STARTED_STAGE = 'objectives.survey.stage.started';
/** `objectiveMetrics` keys: the catalogue's numeric columns (`objectives.ts`). */
const AMOUNT_METRIC = 'committedAmount';
const SCORE_METRIC = 'score';

const div = (num: number, den: number): number | null => (den > 0 ? num / den : null);

/**
 * How many contacts reached one of the objective's stages, from the funnel's
 * `stage:<i>` rows. `i` indexes `results.stageLabels`, which the wizard stores
 * as the objective's stages in order (`objectiveStageLabels`); a campaign
 * whose stage list doesn't line up with the catalogue's gets `null`.
 */
export function stageCount(
  perf: CampaignPerformance,
  objective: CampaignObjectiveId,
  stageKey: string,
): number | null {
  const stages = objectiveById(objective)?.stages ?? [];
  const rows = perf.funnel.filter((r) => r.key.startsWith('stage:'));
  const i = stages.indexOf(stageKey);
  if (i < 0 || rows.length !== stages.length) return null;
  return perf.funnel.find((r) => r.key === `stage:${i}`)?.count ?? null;
}

/**
 * An objective column with at least one value. Voice answers `{ count: 0,
 * sum: 0, avg: null }` when no contact has one: that is "no figure" — a dash,
 * like a column Voice did not send — never $0.00 or a 0.0 score.
 */
const metric = (perf: CampaignPerformance, key: string) => {
  const m = perf.objectiveMetrics.find((x) => x.key === key);
  return m && m.count > 0 && m.avg !== null ? m : null;
};

function build(
  id: KpiId,
  perf: CampaignPerformance,
  objective: CampaignObjectiveId,
  x: KpiExtras,
): Kpi {
  const label = `performance.kpis.${id}`;
  const { calls, contacts } = perf;
  const outcomeRate = (key: string, fn: (n: number, talked: number) => number = (n) => n) => ({
    value: x.outcomes
      ? div(fn(x.outcomes.byKey[key] ?? 0, x.outcomes.talked), x.outcomes.talked)
      : null,
    pending: x.outcomesPending,
  });
  const ofContacted = (n: number | null) => (n === null ? null : div(n, contacts.contacted));
  switch (id) {
    // Of the contacts, the ones whose last call connected.
    case 'contact-rate':
      return { id, label, value: fromPercent(contacts.contactRate), format: 'pct' };
    // Over the conversations, not the dial: escalating is decided TALKING.
    case 'escalation-rate':
      return { id, label, format: 'pct', ...outcomeRate(ESCALATE_OUTCOME) };
    case 'promised-amount': {
      const m = metric(perf, AMOUNT_METRIC);
      return {
        id,
        label,
        value: m ? m.sum : null,
        format: 'money-short',
        hint: m && m.avg !== null ? { value: m.avg, format: 'money' } : undefined,
      };
    }
    case 'dispute-rate':
      return { id, label, format: 'pct', ...outcomeRate(DISPUTE_OUTCOME) };
    case 'qualification-rate':
      return {
        id,
        label,
        value: ofContacted(stageCount(perf, objective, QUALIFIED_STAGE)),
        format: 'pct',
      };
    // Over the surveys STARTED (its first stage); over the contacted when the
    // stages can't be read.
    case 'completion-rate': {
      const started = stageCount(perf, objective, STARTED_STAGE);
      return {
        id,
        label,
        value: div(contacts.conversions, started ?? contacts.contacted),
        format: 'pct',
      };
    }
    // The survey's score column, averaged over who answered it.
    case 'csat': {
      const m = metric(perf, SCORE_METRIC);
      return {
        id,
        label,
        value: m ? m.avg : null,
        format: 'decimal',
        hint: m ? { value: m.count, format: 'int' } : undefined,
      };
    }
    // Delivered = the line answered, person or voicemail: over the calls.
    case 'delivery-rate':
      return {
        id,
        label,
        value: div(calls.connected + calls.voicemail, calls.total),
        format: 'pct',
      };
    // The two halves of the same split over who talked: who gave the reason,
    // and who talked without giving it (the `uncooperative` outcome).
    case 'reason-identified':
      return {
        id,
        label,
        format: 'pct',
        ...outcomeRate(UNCOOPERATIVE_OUTCOME, (n, talked) => Math.max(0, talked - n)),
      };
    case 'uncooperative':
      return { id, label, format: 'pct', ...outcomeRate(UNCOOPERATIVE_OUTCOME) };
    case 'offer-reach':
      return {
        id,
        label,
        value: ofContacted(stageCount(perf, objective, OFFER_STAGE)),
        format: 'pct',
      };
    // Mean of the connected calls, in minutes for `fmtMinutes`.
    case 'avg-duration':
      return {
        id,
        label,
        value: calls.avgDurationSec === null ? null : calls.avgDurationSec / 60,
        format: 'minutes',
      };
    case 'cost-per-contact':
      return {
        id,
        label,
        value: x.costUsd === null ? null : div(x.costUsd, contacts.contacted),
        format: 'money',
      };
  }
}

/** Same two first in every objective: how far the agent gets, and where it stops. */
const HEAD: KpiId[] = ['contact-rate', 'escalation-rate'];
/** The operational tail: how long the call lasted and what it cost. */
const TAIL: KpiId[] = ['avg-duration', 'cost-per-contact'];
/** What only this objective measures. */
const OWN: Record<CampaignObjectiveId, KpiId[]> = {
  collections: ['promised-amount', 'dispute-rate'],
  sales: [],
  scheduling: [],
  survey: ['completion-rate', 'csat'],
  notification: ['delivery-rate'],
  retention: ['reason-identified', 'uncooperative', 'offer-reach'],
};

/** The objective's cards, in order — what the skeleton labels before any figure. */
export function kpiIdsFor(objective: CampaignObjectiveId): KpiId[] {
  return [...HEAD, ...OWN[objective], ...TAIL];
}

/** Which cards carry a second line, so the skeleton reserves it. */
export const KPI_WITH_HINT: ReadonlySet<KpiId> = new Set(['promised-amount', 'csat']);

export function kpisFor(
  perf: CampaignPerformance,
  objective: CampaignObjectiveId,
  extras: KpiExtras = NO_EXTRAS,
): Kpi[] {
  return kpiIdsFor(objective).map((id) => build(id, perf, objective, extras));
}

/** A KPI value as the card shows it; a dash when there is no source or no denominator. */
export function formatKpi(
  value: number | null,
  format: KpiFormat,
  lang: string | undefined,
): string {
  if (value === null) return '—';
  if (format === 'pct') return fmtPct(value, lang);
  if (format === 'money') return fmtMoney(value, lang);
  if (format === 'money-short') return fmtMoneyShort(value, lang);
  if (format === 'minutes') return fmtMinutes(value);
  if (format === 'decimal') return formatNumber(value, lang, { maximumFractionDigits: 1 });
  return fmtInt(value, lang);
}

/* ─── The funnel ─────────────────────────────────────────────────────────── */

/**
 * The drop INTO each of the objectives' own stages: who did not get there
 * (the right party, and each middle rung).
 */
const STAGE_DROPS: Record<string, string> = {
  'objectives.shared.stage.identityVerified': 'performance.funnel.drop.identityVerified',
  'objectives.collections.stage.acknowledges': 'performance.funnel.drop.acknowledged',
  'objectives.collections.stage.negotiation': 'performance.funnel.drop.negotiating',
  'objectives.sales.stage.qualified': 'performance.funnel.drop.qualified',
  'objectives.sales.stage.proposal': 'performance.funnel.drop.pitched',
  'objectives.sales.stage.negotiation': 'performance.funnel.drop.negotiating',
  'objectives.survey.stage.started': 'performance.funnel.drop.surveyStarted',
  'objectives.survey.stage.inProgress': 'performance.funnel.drop.progressing',
  'objectives.retention.stage.diagnosis': 'performance.funnel.drop.diagnosed',
  'objectives.retention.stage.offer': 'performance.funnel.drop.offered',
  'objectives.retention.stage.response': 'performance.funnel.drop.offerResponse',
  'objectives.notification.stage.reached': 'performance.funnel.drop.delivered',
  'objectives.notification.stage.verified': 'performance.funnel.drop.identityVerified',
};

/**
 * How many rows the funnel will have before it arrives, for the skeleton:
 * audience, contacted and one per stage of the objective — the last stage IS
 * the conversion (`funnelStages`).
 */
export function funnelRowCount(objective: CampaignObjectiveId): number {
  return 2 + (objectiveById(objective)?.stages.length ?? 0);
}

export interface FunnelStage {
  /** An i18n key when `translate`, else a label as the campaign stored it. */
  name: string;
  translate: boolean;
  value: number;
  /** Who did NOT reach this row, and why (i18n key). The first has none. */
  drop?: { label: string; count: number };
  /** Of the audience that got here (Voice `ofAudience`). */
  cum?: number;
  /** Of the previous row that got here (Voice `ofPrevious`). */
  step?: number;
}

/**
 * Whether Voice's last `stage:<i>` row is the conversion itself. Every
 * objective's ladder ends on its conversion, and Voice sends that
 * rung twice: as the stored stage (`stage:N`, in the language the campaign was
 * created in) and as `conversion`. It is that rung when the stored stages line
 * up with the catalogue's, or — a stage list of its own — when its label folds
 * to the conversion's (`conversionText`, already translated).
 */
function isConversionStage(
  row: PerformanceFunnelRow,
  stageRows: PerformanceFunnelRow[],
  aligned: boolean,
  conversionText: string | undefined,
): boolean {
  if (row !== stageRows[stageRows.length - 1]) return false;
  if (aligned) return true;
  return (
    !!row.label &&
    !!conversionText &&
    foldOutcomeLabel(row.label) === foldOutcomeLabel(conversionText)
  );
}

/**
 * Voice's funnel as the rows draw it. Voice counts and divides; the front only
 * names the fixed rows (`audience`, `contacted`, `conversion`) and the drop
 * into each. A stage keeps the label the campaign stored for it; its drop is
 * the catalogue's when the stage list lines up with the objective's.
 *
 * The objective's last stage and `conversion` are one rung: drawn
 * once, as the conversion, with Voice's `conversion` count; its step is then
 * measured against the stage before, since Voice's `ofPrevious` was against
 * the stage that is no longer drawn.
 */
export function funnelStages(
  rows: PerformanceFunnelRow[],
  objective: CampaignObjectiveId,
  conversionText?: string,
): FunnelStage[] {
  const def = objectiveById(objective);
  const stages = def?.stages ?? [];
  const stageRows = rows.filter((r) => r.key.startsWith('stage:'));
  const aligned = stageRows.length > 0 && stageRows.length === stages.length;
  const hasConversion = rows.some((r) => r.key === 'conversion');
  const drawn = hasConversion
    ? rows.filter((r) => !isConversionStage(r, stageRows, aligned, conversionText))
    : rows;
  const merged = drawn.length !== rows.length;
  return drawn.map((row, i) => {
    const prev = i > 0 ? drawn[i - 1].count : null;
    const pct = (n: number | null) => (n === null ? undefined : n / 100);
    let name: string;
    let translate = true;
    let drop = 'performance.funnel.noConversion';
    let step = pct(row.ofPrevious);
    if (row.key === 'audience') {
      name = 'performance.funnel.totalAudience';
    } else if (row.key === 'contacted') {
      name = 'performance.funnel.contacted';
      drop = 'performance.funnel.notReachedOrNoAnswer';
    } else if (row.key === 'conversion') {
      name = def?.conversionLabel ?? 'performance.funnel.conversion';
      if (merged) step = prev !== null && prev > 0 ? row.count / prev : undefined;
    } else {
      const index = Number(row.key.slice('stage:'.length));
      const stageKey = aligned ? stages[index] : undefined;
      if (row.label) {
        name = row.label;
        translate = false;
      } else {
        name = stageKey ?? row.key;
        translate = !!stageKey;
      }
      drop = (stageKey && STAGE_DROPS[stageKey]) || 'performance.funnel.dropGeneric';
    }
    return {
      name,
      translate,
      value: row.count,
      drop: prev === null ? undefined : { label: drop, count: Math.max(0, prev - row.count) },
      cum: i === 0 ? undefined : pct(row.ofAudience),
      step: i === 0 ? undefined : step,
    };
  });
}

/* ─── How the calls ended ────────────────────────────────────────────────── */

export type CallEnding = 'connected' | 'voicemail' | 'noAnswer' | 'busy' | 'failed';

/**
 * Every call of the slice by how it ended — a partition of `calls.total`. The
 * order is the legend's and the ramp's, so the darkest step always falls on
 * the best ending.
 */
export function callEndings(calls: PerformanceCalls): { id: CallEnding; count: number }[] {
  return [
    { id: 'connected', count: calls.connected },
    { id: 'voicemail', count: calls.voicemail },
    { id: 'noAnswer', count: calls.noAnswer },
    { id: 'busy', count: calls.busy },
    { id: 'failed', count: calls.failed },
  ];
}

/* ─── The calendar ───────────────────────────────────────────────────────── */

/** Each dialled day of the calendar: calls closed and contacts converted that day. */
export interface DailyResult {
  date: string;
  calls: number;
  conversions: number;
  /** Voice's split of the day by subagent, only when it sends one (pending Voice). */
  bySubagent?: PerformanceDaySubagent[];
}

export const dailyResults = (calendar: PerformanceDay[]): DailyResult[] =>
  calendar.map((d) => ({
    date: d.day,
    calls: d.calls,
    conversions: d.conversions,
    ...(d.bySubagent ? { bySubagent: d.bySubagent } : {}),
  }));

/** One row of the day tooltip's split (`DayTip`'s `rows`). */
export interface DaySubagentRow {
  id: string;
  name: string;
  value: number;
}

/**
 * The day's calls per subagent, as the runway's tooltip lists them — from
 * Voice's REAL split, never from the
 * subagents' shares. No split from Voice → no rows. `name` resolves an id to
 * its label; `null` is the bucket of contacts with no subagent.
 */
export function daySubagentRows(
  day: DailyResult | undefined,
  name: (subagentId: string | null) => string,
): DaySubagentRow[] {
  if (!day?.bySubagent) return [];
  return day.bySubagent.map((s) => ({
    id: s.subagentId ?? '__none__',
    name: name(s.subagentId),
    value: s.calls,
  }));
}

/* ─── The runway of what is left ─────────────────────────────────────────── */

/**
 * The first day what is pending is spread from:
 * the start if it is still ahead; tomorrow once the campaign dialled (today is
 * already spent); otherwise today.
 */
export function pendingFrom(startDate: string, dialled: boolean, today: string): string {
  if (startDate > today) return startDate;
  return dialled ? isoPlusDays(today, 1) : today;
}

/**
 * The runway of what is left, plus whether its pace is known.
 *
 * `paceKnown: false` = the campaign runs with no daily cap: the rate is
 * whatever the lines carry, and that capacity has no source yet (the wizard's
 * `PROVIDER_DAILY_CAP` is a demo figure). Then nothing is projected —
 * `perDay`, `daysNeeded`, `idleDays` and `leftover` are 0, `exhaustsOn` is
 * `null` — and `status` is `fits` only so the calendar still draws what
 * happened and the active days ahead.
 */
export interface PendingRunway extends Runway {
  paceKnown: boolean;
}

/**
 * The runway of what a campaign still has to call, counted from today,
 * at the campaign's OWN quota — never the demo
 * provider ceiling (`ownQuotaPace`). A range already over with contacts left
 * is `short`, not `empty`.
 */
export function pendingRunway(
  pending: number,
  pacing: Pacing,
  startDate: string,
  endDate: string | null,
  dialled: boolean,
  today: string,
): PendingRunway {
  const from = pendingFrom(startDate, dialled, today);
  const pace = ownQuotaPace(pacing);
  if (pace === null) {
    const r = computeRunway(pending, pacing, from, endDate, {
      perDay: 0,
      capacity: 0,
      limitedBy: 'quota',
    });
    const callable = pending > 0 && r.windowMinutes > 0;
    return { ...r, status: callable ? 'fits' : 'empty', paceKnown: false };
  }
  const r = computeRunway(pending, pacing, from, endDate, pace);
  if (r.status === 'empty' && pending > 0 && r.perDay > 0 && endDate !== null && endDate < from) {
    return {
      ...r,
      status: 'short',
      daysNeeded: Math.ceil(pending / r.perDay),
      leftover: pending,
      paceKnown: true,
    };
  }
  return { ...r, paceKnown: true };
}
